// src/features/locker-controllers/types/index.ts
import { RaRecord, Identifier } from 'react-admin';

// ============================================
// INTERFACES BASE
// ============================================

export interface LockerControllerBase {
  id: Identifier;
  name: string;
  ipAddress: string;
  tcpPort: number;
  macAddress?: string;
  isActive: boolean;
  hotelId: string;
  zoneId: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// INTERFACES PARA LISTAS
// ============================================

export interface ZoneSummary {
  id: string;
  name: string;
  description?: string;
}

export interface LockerControllerListItem extends LockerControllerBase, RaRecord {
  zone: ZoneSummary;
  lastSeenAt?: string;
  lockerCount?: number;
  activeLockerCount?: number;
}

// ============================================
// INTERFACES PARA DETALLE
// ============================================

export interface LockerControllerDetailResponse extends LockerControllerBase, RaRecord {
  zone: ZoneSummary;
  lastSeenAt?: string;
  lockerCount?: number;
  activeLockerCount?: number;
  firmware?: string;
  hardwareVersion?: string;
}

// ============================================
// INTERFACES PARA FORMULARIOS
// ============================================

export interface LockerControllerFormData {
  name: string;
  ipAddress: string;
  tcpPort: number;
  macAddress?: string;
  hotelId: string;
  zoneId: string;
  isActive?: boolean;
}

export interface CreateLockerControllerRequest {
  name: string;
  ipAddress: string;
  tcpPort: number;
  macAddress?: string;
  hotelId: string;
  zoneId: string;
}

export interface UpdateLockerControllerRequest {
  name?: string;
  ipAddress?: string;
  tcpPort?: number;
  macAddress?: string;
  zoneId?: string;
  isActive?: boolean;
}

// ============================================
// TYPES PARA FORMATO DE FECHA/CONEXIÓN
// ============================================

export interface LastSeenInfo {
  text: string;
  isOnline: boolean;
  minutesAgo: number;
  hoursAgo: number;
  daysAgo: number;
  formattedDate: string;
}

// ============================================
// CONSTANTES
// ============================================

export const DEFAULT_TCP_PORT = 8080;
export const DEFAULT_CONNECTION_TIMEOUT = 5000; // 5 segundos
export const ONLINE_THRESHOLD_MINUTES = 5; // Considerado online si visto en los últimos 5 minutos

// ============================================
// FUNCIONES HELPER - ESTADO Y CONEXIÓN
// ============================================

/**
 * Determina si un controlador está online basado en su última conexión
 */
export const isOnline = (lastSeenAt: string | undefined): boolean => {
  if (!lastSeenAt) return false;
  
  const now = new Date();
  const lastSeen = new Date(lastSeenAt);
  const diffMs = now.getTime() - lastSeen.getTime();
  const minutesAgo = Math.floor(diffMs / 60000);
  
  return minutesAgo < ONLINE_THRESHOLD_MINUTES;
};

/**
 * Obtiene el color para el estado de conexión
 */
export const getConnectionStatusColor = (lastSeenAt: string | undefined): 'success' | 'warning' | 'error' | 'default' => {
  if (!lastSeenAt) return 'default';
  
  const now = new Date();
  const lastSeen = new Date(lastSeenAt);
  const diffMs = now.getTime() - lastSeen.getTime();
  const minutesAgo = Math.floor(diffMs / 60000);
  
  if (minutesAgo < ONLINE_THRESHOLD_MINUTES) return 'success';
  if (minutesAgo < 60) return 'warning';
  if (minutesAgo < 1440) return 'default'; // < 24 horas
  return 'error';
};

/**
 * Obtiene la etiqueta para el estado de conexión
 */
export const getConnectionStatusLabel = (lastSeenAt: string | undefined): string => {
  const info = formatLastSeen(lastSeenAt);
  return info.text;
};

/**
 * Formatea la última vez que se vio un controlador
 * Retorna un objeto con información estructurada
 */
export const formatLastSeen = (lastSeenAt: string | undefined): LastSeenInfo => {
  if (!lastSeenAt) {
    return {
      text: 'Nunca conectado',
      isOnline: false,
      minutesAgo: Infinity,
      hoursAgo: Infinity,
      daysAgo: Infinity,
      formattedDate: '-',
    };
  }

  const now = new Date();
  const lastSeen = new Date(lastSeenAt);
  const diffMs = now.getTime() - lastSeen.getTime();
  const minutesAgo = Math.floor(diffMs / 60000);
  const hoursAgo = Math.floor(minutesAgo / 60);
  const daysAgo = Math.floor(hoursAgo / 24);

  let text: string;
  const isOnlineStatus = minutesAgo < ONLINE_THRESHOLD_MINUTES;

  if (isOnlineStatus) {
    text = 'En línea';
  } else if (minutesAgo < 60) {
    text = `Hace ${minutesAgo} min`;
  } else if (hoursAgo < 24) {
    text = `Hace ${hoursAgo}h`;
  } else if (daysAgo === 1) {
    text = 'Ayer';
  } else if (daysAgo < 7) {
    text = `Hace ${daysAgo} días`;
  } else if (daysAgo < 30) {
    const weeks = Math.floor(daysAgo / 7);
    text = `Hace ${weeks} ${weeks === 1 ? 'semana' : 'semanas'}`;
  } else {
    const months = Math.floor(daysAgo / 30);
    text = `Hace ${months} ${months === 1 ? 'mes' : 'meses'}`;
  }

  return {
    text,
    isOnline: isOnlineStatus,
    minutesAgo,
    hoursAgo,
    daysAgo,
    formattedDate: lastSeen.toLocaleString('es-ES'),
  };
};

// ============================================
// FUNCIONES DE VALIDACIÓN
// ============================================

/**
 * Valida si una dirección IP es válida (IPv4 o IPv6)
 */
export const isValidIpAddress = (ip: string): boolean => {
  // IPv4
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipv4Regex.test(ip)) {
    const parts = ip.split('.');
    return parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
  }
  
  // IPv6 simplificado
  const ipv6Regex = /^([\da-f]{1,4}:){7}[\da-f]{1,4}$/i;
  return ipv6Regex.test(ip);
};

/**
 * Valida si un puerto TCP es válido
 */
export const isValidTcpPort = (port: number): boolean => {
  return Number.isInteger(port) && port >= 1 && port <= 65535;
};

/**
 * Valida si un nombre de controlador es válido
 */
export const isValidControllerName = (name: string): boolean => {
  return name.length >= 2 && name.length <= 255;
};

/**
 * Valida si una dirección MAC es válida
 */
export const isValidMacAddress = (mac: string): boolean => {
  const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
  return macRegex.test(mac);
};

// ============================================
// FUNCIONES DE TRANSFORMACIÓN
// ============================================

/**
 * Prepara los datos del controlador para enviar a la API
 */
export const prepareLockerControllerForAPI = (
  data: LockerControllerFormData
): CreateLockerControllerRequest => {
  return {
    name: data.name.trim(),
    ipAddress: data.ipAddress.trim(),
    tcpPort: data.tcpPort,
    macAddress: data.macAddress?.trim(),
    hotelId: data.hotelId,
    zoneId: data.zoneId,
  };
};